import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiCheck, FiChevronDown, FiSearch, FiX } from 'react-icons/fi';
import AutoCompleteSelect, {
  IOption,
  InputProps as AutoCompleteSelectProps,
} from '../AutoCompleteSelect';
import * as St from './styles';

export type { IOption };

export type AutoCompleteSelectModalProps = {
  label?: string;
  name?: string;
  /** id da opção selecionada. */
  value?: string | number;
  options: IOption[];
  onChange?: (option: IOption) => void;
  /** true: ao clicar, abre uma modal centralizada com busca para escolher o valor. */
  modal?: boolean;
  /** Título da modal; usa o label quando omitido. */
  modalTitle?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  errorMessage?: string;
  filterFunction?: AutoCompleteSelectProps['filterFunction'];
  /**
   * Busca no servidor (só no modo modal). A partir de `searchMinLength`
   * caracteres, a lista mostra o retorno desta função em vez de filtrar `options`.
   */
  onSearch?: (text: string) => Promise<IOption[]>;
  searchMinLength?: number;
};

const SEARCH_DEBOUNCE = 400;

const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .trim();

const defaultFilter = (options: IOption[], text: string) =>
  options.filter((option) => normalize(option.value).includes(normalize(text)));

const AutoCompleteSelectModal = ({
  label,
  name,
  value,
  options = [],
  onChange = () => null,
  modal = false,
  modalTitle,
  placeholder = 'Selecione',
  searchPlaceholder = 'Buscar',
  disabled = false,
  errorMessage,
  filterFunction = defaultFilter,
  onSearch,
  searchMinLength = 3,
}: AutoCompleteSelectModalProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [remoteOptions, setRemoteOptions] = useState<IOption[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  // Guarda a opção escolhida na busca remota, que pode não estar em `options`.
  const [lastSelected, setLastSelected] = useState<IOption>();
  const searchRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selected =
    options.find((option) => option.id === value) ??
    (lastSelected?.id === value ? lastSelected : undefined);

  const remoteSearch = !!onSearch && search.trim().length >= searchMinLength;

  const filtered = useMemo(() => {
    if (remoteSearch) return remoteOptions ?? [];
    return search ? filterFunction(options, search) : options;
  }, [remoteSearch, remoteOptions, options, search, filterFunction]);

  useEffect(() => {
    if (!remoteSearch) {
      setRemoteOptions(null);
      setSearching(false);
      setSearchError(false);
      return undefined;
    }
    let active = true;
    setSearching(true);
    setSearchError(false);
    const timer = setTimeout(() => {
      onSearch(search.trim())
        .then((result) => active && setRemoteOptions(result))
        .catch(() => {
          if (!active) return;
          setRemoteOptions([]);
          setSearchError(true);
        })
        .finally(() => active && setSearching(false));
    }, SEARCH_DEBOUNCE);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [search, remoteSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    searchRef.current?.focus();
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEsc);
      triggerRef.current?.focus();
    };
  }, [open]);

  if (!modal) {
    return (
      <AutoCompleteSelect
        label={label}
        name={name}
        value={selected?.value}
        options={options}
        onChange={onChange}
        disabled={disabled}
        errorMessage={errorMessage}
        filterFunction={filterFunction}
      />
    );
  }

  const close = () => {
    setOpen(false);
    setSearch('');
  };

  const handleSelect = (option: IOption) => {
    setLastSelected(option);
    onChange(option);
    close();
  };

  const emptyMessage = () => {
    if (searching) return 'Buscando...';
    if (searchError) return 'Não foi possível buscar agora. Tente novamente.';
    if (onSearch && search && !remoteSearch)
      return `Digite ao menos ${searchMinLength} letras para buscar`;
    return 'Sem resultados';
  };

  return (
    <St.Container isFocused={open} disabled={disabled} error={!!errorMessage}>
      {!!label && <label htmlFor={name}>{label}</label>}
      <St.Trigger
        ref={triggerRef}
        id={name}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <span className={selected ? '' : 'placeholder'}>
          {selected?.value ?? placeholder}
        </span>
        <FiChevronDown />
      </St.Trigger>
      {errorMessage && <St.ErrorMessage>{errorMessage}</St.ErrorMessage>}

      {open &&
        ReactDOM.createPortal(
          <St.Overlay onMouseDown={close}>
            <St.Sheet
              role="dialog"
              aria-modal="true"
              aria-label={modalTitle ?? label}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <St.SheetHeader>
                <strong>{modalTitle ?? label}</strong>
                <button type="button" aria-label="Fechar" onClick={close}>
                  <FiX />
                </button>
              </St.SheetHeader>
              <St.Search>
                <FiSearch />
                <input
                  ref={searchRef}
                  value={search}
                  placeholder={searchPlaceholder}
                  autoComplete="off"
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !searching && filtered.length === 1) {
                      handleSelect(filtered[0]);
                    }
                  }}
                />
              </St.Search>
              <St.Options role="listbox">
                {!searching && filtered.length > 0 ? (
                  filtered.map((option) => {
                    const isSelected = option.id === value;
                    return (
                      <St.Option
                        key={option.id ?? option.value}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        selected={isSelected}
                        onClick={() => handleSelect(option)}
                      >
                        <span>{option.value}</span>
                        {isSelected && <FiCheck />}
                      </St.Option>
                    );
                  })
                ) : (
                  <St.Empty role="status">{emptyMessage()}</St.Empty>
                )}
              </St.Options>
            </St.Sheet>
          </St.Overlay>,
          document.body,
        )}
    </St.Container>
  );
};

export default AutoCompleteSelectModal;
